import * as z from "zod";

export const BOM_CSV_COLUMNS = {
  BOM_NAME: "BOM Name",
  OUTPUT_SKU: "Output SKU",
  OUTPUT_QUANTITY: "Output Quantity",
  COMPONENT_SKU: "Component SKU",
  COMPONENT_QUANTITY: "Component Quantity",
} as const;

const COLS = BOM_CSV_COLUMNS;

const positiveInteger = (label: string) =>
  z.preprocess(
    (value) => {
      if (typeof value !== "string") {
        return value;
      }
      const number = Number(value.trim());
      return Number.isInteger(number) ? number : -1;
    },
    z.number().int().positive(`"${label}" must be a positive integer`),
  );

export const BomCsvRow = z.object({
  name: z.string().trim().min(1, `"${COLS.BOM_NAME}" is required`).max(255),
  outputSku: z
    .string()
    .trim()
    .min(1, `"${COLS.OUTPUT_SKU}" is required`)
    .max(255),
  outputQuantity: positiveInteger(COLS.OUTPUT_QUANTITY),
  componentSku: z
    .string()
    .trim()
    .min(1, `"${COLS.COMPONENT_SKU}" is required`)
    .max(255),
  componentQuantity: positiveInteger(COLS.COMPONENT_QUANTITY),
});
export type BomCsvRow = z.infer<typeof BomCsvRow>;

export interface ParsedBomCsvRow {
  rowNumber: number;
  data: BomCsvRow;
}

export interface ParsedBomCsv {
  rows: ParsedBomCsvRow[];
  parseErrors: string[];
}

export function parseBomCsvRows(
  rows: readonly (readonly string[])[],
): ParsedBomCsv {
  const header = rows[0] ?? [];
  const columnIndex = (column: string) =>
    header.findIndex(
      (cell) => cell.trim().toLowerCase() === column.toLowerCase(),
    );
  const indices = {
    name: columnIndex(COLS.BOM_NAME),
    outputSku: columnIndex(COLS.OUTPUT_SKU),
    outputQuantity: columnIndex(COLS.OUTPUT_QUANTITY),
    componentSku: columnIndex(COLS.COMPONENT_SKU),
    componentQuantity: columnIndex(COLS.COMPONENT_QUANTITY),
  };
  const missing = Object.entries(indices)
    .filter(([, index]) => index < 0)
    .map(
      ([key]) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ({
          name: COLS.BOM_NAME,
          outputSku: COLS.OUTPUT_SKU,
          outputQuantity: COLS.OUTPUT_QUANTITY,
          componentSku: COLS.COMPONENT_SKU,
          componentQuantity: COLS.COMPONENT_QUANTITY,
        })[key]!,
    );
  if (missing.length > 0) {
    return {
      rows: [],
      parseErrors: [
        `Missing required columns: ${missing.map((column) => `"${column}"`).join(", ")}.`,
      ],
    };
  }

  const parsedRows: ParsedBomCsvRow[] = [];
  const parseErrors: string[] = [];
  for (let index = 1; index < rows.length; index++) {
    const row = rows[index] ?? [];
    if (!row.some((cell) => cell.trim() !== "")) {
      continue;
    }
    const result = BomCsvRow.safeParse({
      name: row[indices.name] ?? "",
      outputSku: row[indices.outputSku] ?? "",
      outputQuantity: row[indices.outputQuantity] ?? "",
      componentSku: row[indices.componentSku] ?? "",
      componentQuantity: row[indices.componentQuantity] ?? "",
    });
    if (!result.success) {
      parseErrors.push(
        `Row ${index + 1}: ${result.error.issues.map((issue) => issue.message).join("; ")}`,
      );
    } else {
      parsedRows.push({ rowNumber: index + 1, data: result.data });
    }
  }
  return { rows: parsedRows, parseErrors };
}

export const ImportBomCsvResult = z.object({
  createdCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  errors: z.array(
    z.object({ rowNumber: z.number().int().positive(), message: z.string() }),
  ),
  parseErrors: z.array(z.string()),
});
export type ImportBomCsvResult = z.infer<typeof ImportBomCsvResult>;

export const BOM_CSV_TEMPLATE_LINES: readonly (readonly string[])[] = [
  Object.values(COLS),
  ["Starter Kit", "KIT-001", "1", "COMPONENT-A", "2"],
  ["Starter Kit", "KIT-001", "1", "COMPONENT-B", "1"],
];
